<template>
  <v-autocomplete :model-value="modelValue" :placeholder="$t('NAV.LOOK_FOR_BUBBLE')" autocomplete="off"
                  :items="filteredItems" :item-title="getElTitle" item-value="id" return-object
                  @update:model-value="$emit('update:modelValue', $event)" @click="filtersMenuIsOpen = false" v-bind="$attrs">
    <template #item="{ props, item: { title, raw: item } }">
      <v-divider v-if="item.divider"></v-divider>
      <v-list-subheader v-else-if="item.header" :title="item.header"></v-list-subheader>
      <v-list-item v-else v-bind="props">
        <template #title>
          <select-element-item :item="item" :planning-locale="planningLocale" align-icons :title="title"></select-element-item>
        </template>
      </v-list-item>
    </template>
    <template #selection="{ item: { title, raw: item } }">
      <select-element-item :item="item" :planning-locale="planningLocale" :title="title"></select-element-item>
    </template>

    <!-- FILTERS -->
    <template #prepend>
      <v-menu v-model="filtersMenuIsOpen" :close-on-content-click="false">
        <template #activator="{ props }">
          <v-btn density="comfortable" color="secondary" icon v-bind="props">
            <v-badge :model-value="hasFilterActive" color="successgreen" floating dot>
              <v-icon>far fa-filter</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <v-card class="pa-6">
          <div>
            {{ $t('EXPORT.LANES') }}
            <select-filter v-model="selectedLanesList" :items="availableLanes"
                           item-value="id" item-title="label"
                           multiple>
              <template #prepend-item v-if="availableLanesIds.length">
                <filter-toggle :selected-ids="selectedLanesList" :sublist-ids="availableLanesIds"
                               @update="selectedLanesList = $event">
                  {{ $t('EXPORT.ALL_LANES') }} ({{ availableLanesIds.length }})
                </filter-toggle>
                <v-divider></v-divider>
              </template>
              <template #selection="{ item: { raw: item }, index }">
                <span v-if="selectedLanesList.length == 1">{{ item.label }}</span>
                <span v-else-if="selectedLanesList.length == availableLanes.length && index === 0" class="nobr">{{ $t('EXPORT.ALL_LANES') }} ({{ availableLanes.length }})</span>
                <span v-else-if="index === 0">{{ selectedLanesList.length }}/{{ availableLanes.length }}</span>
              </template>
            </select-filter>
          </div>

          <div class="mt-1 d-flex gap-4">
            <div style="flex: 1 1 50%; min-width: 120px">
              <label for="subtasksFilterFrom">{{ $t('ELEMENT-DETAIL.FROM') }}</label>
              <date-field v-model="startTimeFilter" id="subtasksFilterFrom" filter clearable></date-field>
            </div>
            <div style="flex: 1 1 50%; min-width: 120px">
              <label for="subtasksFilterto">{{ $t('ELEMENT-DETAIL.TO') }}</label>
              <date-field v-model="endTimeFilter" id="subtasksFilterto" filter clearable></date-field>
            </div>
          </div>
        </v-card>
      </v-menu>
    </template>
  </v-autocomplete>
</template>

<script>
  import SelectElementItem from './SelectElementItem.vue';

  export default {
    components: {
      SelectElementItem,
    },
    props: {
      modelValue: { type: Object, default: null },
      items: { type: Array, required: true },
    },
    emits: ['update:modelValue'],
    data() {
      return {
        filtersMenuIsOpen: false,
        selectedLanesList: [],
        startTimeFilter: null,
        endTimeFilter: null,
      };
    },
    computed: {
      itemsByLaneId() {
        const map = new Map();
        this.items.forEach((item) => {
          const laneId = item.getLaneId?.();
          if (! laneId) return;
          if (! map.has(laneId)) map.set(laneId, []);
          map.get(laneId).push(item);
        });
        return map;
      },
      availableLanes() {
        const [someLaneId] = this.itemsByLaneId.keys();
        if (! someLaneId) return [];
        return this.itemsByLaneId.get(someLaneId)[0].getPlanning()?.lanes || [];
      },
      availableLanesIds() {
        return this.availableLanes.map(item => item.id);
      },
      hasFilterActive() {
        return this.selectedLanesList.length != this.availableLanes.length || this.startTimeFilter !== null || this.endTimeFilter !== null;
      },
      filteredItems() {
        if (! this.hasFilterActive) return this.items;

        const selectedLanesSet = new Set(this.selectedLanesList);
        const filteredItems = this.items.filter((item) => {
          if (item.header) return true;
          if (! item.getLaneId) return false; // not an element
          if (! selectedLanesSet.has(item.getLaneId())) return false;
          if (this.startTimeFilter || this.endTimeFilter) {
            const starttime = item.getStartTime();
            const endtime = item.getEndTime();
            if (this.startTimeFilter && ! (endtime || starttime)?.isSameOrAfter(this.startTimeFilter)) return false; // if starttime or endtime is null, use the other as fallback
            if (this.endTimeFilter && ! (starttime || endtime)?.isSameOrBefore(this.endTimeFilter)) return false;
          }
          return true;
        });

        return filteredItems.filter((item, index) => { // remove headers of empty sections
          if (! item.header) return true;
          const nextItem = filteredItems[index + 1];
          return (nextItem && ! nextItem.header) && true || false;
        });
      },
      planningLocale() {
        return this.items.find(item => item.getPlanning)?.getPlanning()?.timeline?.getTimelocale() || 'en';
      },
    },
    created() {
      this.selectedLanesList = this.availableLanes.map(item => item.id);
    },
    methods: {
      getElTitle(el) {
        if (! el.getTitle) return ''; // headers and dividers
        return el.getTitle() || this.$t('helpers.elDefaultTitle', { type: el.getType() });
      },
    },
  };
</script>
